/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/device/resolver.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)    | Who               | Why                    | What Changed         |
 * |---|----------------------|-------------------|------------------------|----------------------|
 * | 1 | 2026-06-16 (Step 7)  | Claude Sonnet 4.6 | Replay v0 (Step 7)     | Created from scratch |
 * | 2 | 2026-06-16 (edit)    | Claude Sonnet 4.6 | Step editing / reliability | Implement relative strategy; add matchedBy to ResolvedTarget; add flattenNodes |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/device/replayer.ts      (resolveTarget called per step)
 * - apps/studio/src/device/resolver.test.ts (unit tests)
 * - (Phase 1) packages/runner/src/resolver.ts will supersede this with
 *   the full Tier-0 auto-wait loop and Tier-1 ONNX healing.
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * - ResolvedTarget   — { node, x, y, matchedBy }
 * - resolveTarget(selector, nodes): ResolvedTarget | null
 *   Tries each candidate in selector.candidates in strict order.
 *   Returns the center-point + winning strategy of the first matched node, or null.
 *
 * CALLER → CALLEE MAP
 * --------------------
 * replayer.ts → resolveTarget(selector, nodes)
 *                   → (if relative) resolveRelative(value, nodes)
 *                       → flattenNodes(nodes)
 *                       → find anchor → find closest node below
 *                   → (otherwise)   findByCandidate(candidate, nodes)
 *                       → matchesCandidate(node, candidate)
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. resolveTarget() is PURE — no ADB, no browser APIs. Safe to unit-test
 *    with an in-memory tree (no real device needed).
 * 2. Candidate.index is NOT implemented in Phase 0 (always returns first match).
 *    Phase 1 runner will implement Nth-match via collect-all-then-pick.
 * 3. DFS order is pre-order (parent before children). First match wins.
 * 4. `relative` is now IMPLEMENTED for the `below(text='X')` and
 *    `below(a11yId='X')` forms generated by buildRelativeCandidate in recorder.ts.
 *    The resolver finds the anchor node by text or resourceId, then returns the
 *    tree node whose top edge is closest below the anchor's bottom edge within
 *    a horizontal tolerance band (anchorHalfWidth + 100 px).
 * 5. `matchedBy` in the return value tells callers which candidate strategy won.
 *    This drives the confidence badge in the replay results UI.
 * 6. Platform-specific strategies (iosPredicate, iosClassChain, css, xpath)
 *    always return false in Phase 0. Phase 1 adds their DAL mapping.
 * 7. textRegex compiles a new RegExp on every call — acceptable for Phase 0.
 *
 * ISSUES FACED / KNOWN GOTCHAS
 * -----------------------------
 * - The relative resolver uses the anchor's half-width + 100 px as a horizontal
 *   tolerance. This is slightly looser than the recorder (which uses the TARGET's
 *   half-width + 50 px) because at resolve time we don't know the target's width
 *   in advance. A mismatch is harmless — false-positive relatives are rare since
 *   the closest-below node is almost always the intended target.
 *
 * =============================================================================
 */

import type { Selector, Candidate } from "@mimiq/ir";
import type { A11yNode } from "./treeParser.js";
import { evaluateXPath } from "./xpathUtils.js";

// ── Return type ───────────────────────────────────────────────────────────────

export interface ResolvedTarget {
  node: A11yNode;
  x: number;   // center X in device pixels
  y: number;   // center Y in device pixels
  /** Which candidate strategy resolved this target — drives confidence badge. */
  matchedBy: string;
}

// ── Internal helpers ──────────────────────────────────────────────────────────

function flattenNodes(nodes: A11yNode[]): A11yNode[] {
  const result: A11yNode[] = [];
  function walk(ns: A11yNode[]) {
    for (const n of ns) { result.push(n); walk(n.children); }
  }
  walk(nodes);
  return result;
}

// Parses below(text='X') or below(a11yId='X') and finds the closest node below
// the anchor node. Mirrors the proximity logic in recorder.ts buildRelativeCandidate.
const BELOW_RE = /^below\((text|a11yId)='([^']*)'\)$/;

function resolveRelative(value: string, nodes: A11yNode[]): A11yNode | null {
  const m = value.match(BELOW_RE);
  if (!m) return null;

  const anchorStrategy = m[1] as "text" | "a11yId";
  const anchorValue = m[2]!;

  const flat = flattenNodes(nodes);

  // Find the anchor — first DFS match (same order as findByCandidate)
  const anchor = flat.find((n) =>
    anchorStrategy === "text" ? n.text === anchorValue : n.resourceId === anchorValue,
  );
  if (!anchor) return null;

  const anchorCx = (anchor.bounds.left + anchor.bounds.right) / 2;
  const anchorHalfW = (anchor.bounds.right - anchor.bounds.left) / 2;

  // Find the node whose top is closest below the anchor's bottom, within
  // the anchor's horizontal extent + 100 px tolerance.
  let best: A11yNode | null = null;
  let bestDist = Infinity;

  for (const node of flat) {
    if (node === anchor) continue;
    // Must be below anchor's bottom edge
    if (node.bounds.top < anchor.bounds.bottom) continue;
    // Must be horizontally aligned with the anchor
    const nodeCx = (node.bounds.left + node.bounds.right) / 2;
    if (Math.abs(nodeCx - anchorCx) > anchorHalfW + 100) continue;

    const dist = node.bounds.top - anchor.bounds.bottom;
    if (dist < bestDist) { bestDist = dist; best = node; }
  }

  return best;
}

// ── Internal: single-node predicate ──────────────────────────────────────────

function matchesCandidate(node: A11yNode, candidate: Candidate): boolean {
  switch (candidate.strategy) {
    case "a11yId":
      return node.resourceId === candidate.value;

    case "text":
      return node.text === candidate.value;

    case "textContains":
      return node.text.includes(candidate.value);

    case "textRegex":
      try {
        return new RegExp(candidate.value).test(node.text);
      } catch {
        return false;
      }

    case "contentDesc":
      return node.contentDesc === candidate.value;

    case "className":
      return node.className === candidate.value;

    // `relative` is handled before this function is called — never reaches here.
    // Phase 5 strategies are not implemented in Phase 0.
    case "relative":
    case "iosPredicate":
    case "iosClassChain":
    case "css":
    case "xpath":
      return false;
  }
}

// ── Internal: pre-order DFS tree search ──────────────────────────────────────

/**
 * Collect ALL nodes matching the candidate, then return the one at candidate.index.
 *
 * IMPORTANT: sorted by visual position (bounds.top → bounds.left) rather than
 * DFS tree order. DFS order does not match visual row order in RecyclerView /
 * Compose LazyColumn — the accessibility tree can enumerate view holders in
 * recycling order which is unrelated to their vertical screen position.
 * Sorting by bounds.top ensures index=0 is the topmost visible row,
 * index=1 is the next row down, etc., matching what the user sees.
 */
function findByCandidate(candidate: Candidate, nodes: A11yNode[]): A11yNode | null {
  const matches: A11yNode[] = [];
  function collect(nodeList: A11yNode[]): void {
    for (const node of nodeList) {
      if (matchesCandidate(node, candidate)) matches.push(node);
      collect(node.children);
    }
  }
  collect(nodes);

  // Sort by visual top-to-bottom, left-to-right position
  matches.sort((a, b) => {
    const topDiff = a.bounds.top - b.bounds.top;
    return topDiff !== 0 ? topDiff : a.bounds.left - b.bounds.left;
  });

  const idx = candidate.index ?? 0;
  return matches[idx] ?? null;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Try each candidate in order. Return the center point + winning strategy of
 * the first node that matches, or null if nothing matched.
 *
 * `relative` candidates are resolved via resolveRelative() (tree-aware).
 * All other candidates use findByCandidate() (per-node predicate DFS).
 *
 * This is Tier-0 resolution (deterministic, no AI). Phase 1 runner wraps
 * this in an auto-wait loop; Phase 2 adds Tier-1 ONNX re-ranking on failure.
 */
export function resolveTarget(
  selector: Selector,
  nodes: A11yNode[],
): ResolvedTarget | null {
  for (const candidate of selector.candidates) {
    let node: A11yNode | null;

    if (candidate.strategy === "relative") {
      node = resolveRelative(candidate.value, nodes);
    } else if (candidate.strategy === "xpath") {
      // XPath requires full-tree evaluation, not per-node predicate matching
      node = evaluateXPath(candidate.value, nodes);
    } else {
      node = findByCandidate(candidate, nodes);
    }

    if (node) {
      return {
        node,
        x: Math.round((node.bounds.left + node.bounds.right) / 2),
        y: Math.round((node.bounds.top + node.bounds.bottom) / 2),
        matchedBy: candidate.strategy,
      };
    }
  }
  return null;
}
